// components/modals/ClinicSettingsModal.tsx
import React, { useContext, useEffect, useState } from "react";
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  ScrollView,
  TextInput,
} from "react-native";
import { useNavigate } from "react-router-dom";
import { SessionContext } from "../../../contexts";
import Tab2 from "./Tab2";
import PrimaryButton from "./PrimaryButton";
import {
  getClinicDetails,
  updateClinicDetails,
  TClinicDetails,
} from "../../../services/api-settings";
import {
  postClinicPhoto,
  postClinicProfile,
} from "../../../services/api-clinic";
import { InputPatientDetailSurgeonPhoto } from "../web";

type Props = {
  visible: boolean;
  onClose: () => void;
};

const ClinicSettingsModal: React.FC<Props> = ({ visible, onClose }) => {
  const sessionContext = useContext(SessionContext);
  const session = sessionContext?.session;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [details, setDetails] = useState<TClinicDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<"Clinic" | "Account">(
    "Clinic",
  );

  const [syncTiming, setSyncTiming] = useState<
    "on_note_creation" | "on_signature"
  >("on_signature");
  const [timezone, setTimezone] = useState<string>("");
  const [leadSurgeonName, setLeadSurgeonName] = useState<string>("");
  const [clinicAddress, setClinicAddress] = useState<string>("");
  const [leadSurgeonPhoto, setLeadSurgeonPhoto] = useState<
    Blob | string | null
  >(null);

  useEffect(() => {
    if (visible && session) {
      setSelectedTab("Clinic");
      loadSettings();
    }
  }, [visible, session]);

  const loadSettings = async () => {
    if (!session) return;

    try {
      setLoading(true);
      setError(null);
      const data = await getClinicDetails(session);
      setDetails(data);
      setSyncTiming(data.note_integration_sync_timing);
      setTimezone(data.timezone || "");
      setLeadSurgeonName(session?.surgeons?.[0]?.name || "");
      setClinicAddress(session?.surgeons?.[0]?.address || "");
      setLeadSurgeonPhoto(session?.surgeons?.[0]?.photoUrl || null);
    } catch (err) {
      console.error("Failed to load clinic settings:", err);
      setError(err instanceof Error ? err.message : "Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!session) return;

    try {
      setSaving(true);
      setError(null);

      const updates = {
        note_integration_sync_timing: syncTiming,
        timezone: timezone || null,
      };

      const updated = await updateClinicDetails(session, updates);
      setDetails(updated);
      let photoUrl = session?.surgeons?.[0]?.photoUrl || "";
      if (leadSurgeonPhoto instanceof Blob) {
        const uploadedPhoto = await postClinicPhoto(session, {
          imageBlob: leadSurgeonPhoto,
        });
        photoUrl = uploadedPhoto.photo_url;
      } else if (typeof leadSurgeonPhoto === "string") {
        photoUrl = leadSurgeonPhoto;
      }
      const updatedProfile = await postClinicProfile(session, {
        surgeon_info: {
          name: leadSurgeonName,
          clinic_address: clinicAddress,
          photo_url: photoUrl,
        },
      });

      if (sessionContext?.setSession) {
        sessionContext.setSession((previousSession) => {
          if (!previousSession) return previousSession;
          const currentFirstSurgeon = previousSession.surgeons?.[0] || {
            name: "",
            address: "",
            photoUrl: "",
          };
          const nextFirstSurgeon = {
            ...currentFirstSurgeon,
            name:
              updatedProfile.surgeon_info?.name?.trim() ??
              leadSurgeonName.trim(),
            address:
              updatedProfile.surgeon_info?.clinic_address?.trim() ??
              clinicAddress.trim(),
            photoUrl:
              updatedProfile.surgeon_info?.photo_url ??
              currentFirstSurgeon.photoUrl,
          };
          const remainingSurgeons = previousSession.surgeons.slice(1);
          return {
            ...previousSession,
            surgeons: [nextFirstSurgeon, ...remainingSurgeons],
          };
        });
      }

      // Close after successful save
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (err) {
      console.error("Failed to update clinic settings:", err);
      setError(
        err instanceof Error ? err.message : "Failed to update settings",
      );
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = () => {
    if (!details) return false;
    return (
      syncTiming !== details.note_integration_sync_timing ||
      (timezone || null) !== details.timezone ||
      leadSurgeonName !== (session?.surgeons?.[0]?.name || "") ||
      clinicAddress !== (session?.surgeons?.[0]?.address || "") ||
      leadSurgeonPhoto !== (session?.surgeons?.[0]?.photoUrl || null)
    );
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View style={styles.card}>
          <Text style={styles.title}>Settings</Text>
          <Tab2
            tabs={["Clinic", "Account"]}
            initialTab="Clinic"
            activeTab={selectedTab}
            onTabSwitch={(tab) => setSelectedTab(tab as "Clinic" | "Account")}
            containerStyle={styles.tabContainer}
          />

          {selectedTab === "Clinic" && loading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color="#4F46E5" />
            </View>
          ) : selectedTab === "Clinic" ? (
            <ScrollView style={styles.scrollContent}>
              {error && (
                <View style={styles.errorContainer}>
                  <Text style={styles.errorText}>{error}</Text>
                </View>
              )}

              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Note Integration Sync</Text>
                <Text style={styles.sectionDesc}>
                  When should notes be synced with your EHR/EMR integration?
                </Text>

                <View style={styles.radioGroup}>
                  <TouchableOpacity
                    style={styles.radioOption}
                    onPress={() => setSyncTiming("on_note_creation")}
                  >
                    <View style={styles.radio}>
                      {syncTiming === "on_note_creation" && (
                        <View style={styles.radioSelected} />
                      )}
                    </View>
                    <View style={styles.radioLabel}>
                      <Text style={styles.radioText}>On Note Creation</Text>
                      <Text style={styles.radioSubtext}>
                        Sync immediately when note is created
                      </Text>
                    </View>
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={styles.radioOption}
                    onPress={() => setSyncTiming("on_signature")}
                  >
                    <View style={styles.radio}>
                      {syncTiming === "on_signature" && (
                        <View style={styles.radioSelected} />
                      )}
                    </View>
                    <View style={styles.radioLabel}>
                      <Text style={styles.radioText}>On Signature</Text>
                      <Text style={styles.radioSubtext}>
                        Sync only after note is signed
                      </Text>
                    </View>
                  </TouchableOpacity>
                </View>
              </View>

              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Timezone</Text>
                <Text style={styles.sectionDesc}>
                  IANA timezone (e.g., America/New_York, Europe/London,
                  Asia/Tokyo)
                </Text>
                <TextInput
                  style={styles.textInput}
                  value={timezone}
                  onChangeText={setTimezone}
                  placeholder="America/New_York"
                  placeholderTextColor="#9CA3AF"
                  autoCapitalize="none"
                  autoCorrect={false}
                />
              </View>

              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Lead surgeon name</Text>
                <TextInput
                  style={styles.textInput}
                  value={leadSurgeonName}
                  onChangeText={setLeadSurgeonName}
                  placeholder="Dr. Jane Smith"
                  placeholderTextColor="#9CA3AF"
                />
              </View>

              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Clinic address</Text>
                <TextInput
                  style={styles.textInput}
                  value={clinicAddress}
                  onChangeText={setClinicAddress}
                  placeholder="123 Main St, City, State"
                  placeholderTextColor="#9CA3AF"
                />
              </View>

              <View style={styles.section}>
                <InputPatientDetailSurgeonPhoto
                  name="Lead surgeon photo"
                  value={leadSurgeonPhoto}
                  onPhotoSelect={setLeadSurgeonPhoto}
                />
              </View>
            </ScrollView>
          ) : (
            <View style={styles.accountContainer}>
              <Text style={styles.sectionTitle}>Account Settings</Text>
              <Text style={styles.sectionDesc}>
                Need to update your password? We will send a secure reset link
                to your account email.
              </Text>
              <PrimaryButton
                text="Reset Password"
                fullWidth
                onPress={() => {
                  onClose();
                  navigate("/forgot-password");
                }}
              />
            </View>
          )}

          <View style={styles.actions}>
            <TouchableOpacity
              style={[styles.btn, styles.btnGhost]}
              onPress={onClose}
              disabled={saving}
            >
              <Text style={styles.btnGhostText}>
                {selectedTab === "Clinic" ? "Cancel" : "Close"}
              </Text>
            </TouchableOpacity>
            {selectedTab === "Clinic" && (
              <TouchableOpacity
                style={[
                  styles.btn,
                  styles.btnPrimary,
                  (!hasChanges() || saving) && styles.btnDisabled,
                ]}
                onPress={handleSave}
                disabled={!hasChanges() || saving}
              >
                {saving ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Text style={styles.btnPrimaryText}>Save</Text>
                )}
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.45)",
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  card: {
    width: "100%",
    maxWidth: 540,
    maxHeight: "90%",
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: 20,
  },
  tabContainer: {
    marginBottom: 16,
  },
  title: {
    fontSize: 18,
    fontWeight: "700",
    marginBottom: 16,
    color: "#111827",
  },
  loadingContainer: {
    paddingVertical: 40,
    alignItems: "center",
  },
  scrollContent: {
    maxHeight: 500,
  },
  accountContainer: {
    paddingBottom: 20,
  },
  errorContainer: {
    backgroundColor: "#FEE2E2",
    padding: 12,
    borderRadius: 8,
    marginBottom: 16,
  },
  errorText: {
    color: "#991B1B",
    fontSize: 14,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: "600",
    color: "#111827",
    marginBottom: 4,
  },
  sectionDesc: {
    fontSize: 13,
    color: "#6B7280",
    marginBottom: 12,
  },
  radioGroup: {
    gap: 12,
  },
  radioOption: {
    flexDirection: "row",
    alignItems: "flex-start",
    padding: 12,
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  radio: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: "#4F46E5",
    marginRight: 12,
    marginTop: 2,
    justifyContent: "center",
    alignItems: "center",
  },
  radioSelected: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: "#4F46E5",
  },
  radioLabel: {
    flex: 1,
  },
  radioText: {
    fontSize: 14,
    fontWeight: "600",
    color: "#111827",
    marginBottom: 2,
  },
  radioSubtext: {
    fontSize: 13,
    color: "#6B7280",
  },
  textInput: {
    padding: 12,
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#D1D5DB",
    fontSize: 14,
    color: "#111827",
  },
  actions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: "#E5E7EB",
  },
  btn: {
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 8,
    minWidth: 80,
    alignItems: "center",
  },
  btnGhost: {
    backgroundColor: "#F3F4F6",
  },
  btnGhostText: {
    color: "#374151",
    fontWeight: "600",
  },
  btnPrimary: {
    backgroundColor: "#4F46E5",
  },
  btnPrimaryText: {
    color: "#fff",
    fontWeight: "700",
  },
  btnDisabled: {
    backgroundColor: "#D1D5DB",
  },
});

export default ClinicSettingsModal;
